import { api } from "@bindery/convex-reader/api";
import { safeReturnPath } from "@bindery/shared/auth/returnPath";
import { Button } from "@bindery/ui/components/ui/button";
import { Checkbox } from "@bindery/ui/components/ui/checkbox";
import {
  ArrowRightEndOnRectangleIcon,
  SparklesIcon,
} from "@heroicons/react/20/solid";
import { useQuery } from "convex/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { type AuthActions, browserAuthActions } from "@/auth/actions";
import {
  alwaysUsesGoogle,
  forgetAlwaysGoogle,
  markGoogleAttempt,
  recentGoogleAttempt,
  rememberAlwaysGoogle,
} from "@/auth/google-preference";
import {
  readerIdOf,
  type SessionStore,
  sessionStore,
  useSession,
} from "@/convex/session";

type ServerProgress = boolean | { readerMismatch: true } | null | undefined;

/**
 * Local rows count as progress before the server has them, and an
 * unanswered check is no answer: a "false" read off a loading query would
 * sign the reader in without offering the progress it has.
 */
export function progressAnswer(
  local: boolean | undefined,
  server: ServerProgress,
): boolean | undefined {
  if (local === true || server === true) return true;
  if (local === undefined || server === undefined) return undefined;
  if (server !== null && server !== false) return undefined;
  return false;
}

// Long enough for a cold Convex connection, short enough that a reader is
// not left facing disabled buttons when the check never answers.
export const PROGRESS_WAIT_MS = 5_000;

/**
 * An answer that never came offers the reader: the broker then asks before
 * adding it, where "no progress" would sign in past progress it never saw.
 */
export function progressAfterWait(
  answer: boolean | undefined,
  waitedOut: boolean,
): boolean | undefined {
  return answer ?? (waitedOut ? true : undefined);
}

function useWaitedOut(waiting: boolean, ms: number): boolean {
  const [waitedOut, setWaitedOut] = useState(false);
  useEffect(() => {
    if (!waiting) return;
    const timer = setTimeout(() => setWaitedOut(true), ms);
    return () => clearTimeout(timer);
  }, [waiting, ms]);
  return waitedOut;
}

// Codes the Worker's /token route and ../app's links put on /sign-in.
const SIGN_IN_ERRORS: Readonly<Record<string, string>> = {
  missing_token:
    "That sign-in link is incomplete. Try scanning the card again.",
  invalid_token: "That sign-in card or link didn't work. It may have expired.",
  token_failed: "We couldn't check that card just now. Please try again.",
  google_sign_in_failed:
    "Google sign-in didn't finish. You can try again or sign in another way.",
};
const GENERIC_SIGN_IN_ERROR = "That sign-in didn't work. Please try again.";

export function signInErrorMessage(code: string | null): string | null {
  if (code === null) return null;
  return SIGN_IN_ERRORS[code] ?? GENERIC_SIGN_IN_ERROR;
}

export interface GooglePreference {
  get: () => boolean;
  remember: () => void;
  forget: () => void;
  markAttempt: () => void;
  recentAttempt: () => boolean;
}

const deviceGooglePreference: GooglePreference = {
  get: () => alwaysUsesGoogle(),
  remember: () => rememberAlwaysGoogle(),
  forget: () => forgetAlwaysGoogle(),
  markAttempt: () => markGoogleAttempt(),
  recentAttempt: () => recentGoogleAttempt(),
};

// Long enough to read the line and reach "Stop always using Google", which is
// what keeps a checked "always use Google" undoable.
export const AUTO_GOOGLE_DELAY_MS = 2_500;

/** With "always use Google" set, leave for Google once the page can. */
function useAutoGoogle(
  active: boolean,
  continueWithGoogle: () => void,
  delayMs: number,
): void {
  const latest = useRef(continueWithGoogle);
  useEffect(() => {
    latest.current = continueWithGoogle;
  });
  useEffect(() => {
    if (!active) return;
    const timer = setTimeout(() => latest.current(), delayMs);
    return () => clearTimeout(timer);
  }, [active, delayMs]);
}

// Back from Google restores this page from the bfcache as it was left:
// on "Continuing with Google…", its timer already spent. The person came
// back, so they choose.
function useChooserOnRestore(showChooser: () => void): void {
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) showChooser();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [showChooser]);
}

const checkLocalProgress = (readerId: string): Promise<boolean> =>
  Promise.all([import("@/sync"), import("@/library/local-progress")]).then(
    ([sync, local]) => local.hasLocalProgress(sync.readerDb, readerId),
  );

function useLocalProgress(
  readerId: string | null,
  check: (readerId: string) => Promise<boolean>,
): boolean | undefined {
  const [answer, setAnswer] = useState<{
    readerId: string;
    hasProgress: boolean;
  } | null>(null);
  useEffect(() => {
    if (readerId === null) return;
    let live = true;
    const settle = (hasProgress: boolean) => {
      if (live) setAnswer({ readerId, hasProgress });
    };
    check(readerId).then(settle, () => settle(false));
    return () => {
      live = false;
    };
  }, [readerId, check]);
  return answer?.readerId === readerId ? answer.hasProgress : undefined;
}

export interface SignInHandlers {
  continueWithGoogle: () => void;
  switchReader: () => void;
  fullAccount: () => void;
}

/**
 * Google carries the reader only when it has progress worth keeping:
 * without it the broker signs straight into the account's linked reader,
 * with it the broker offers to add this one. A full account always names
 * the device's reader, so the broker can ask to link it.
 */
export function signInHandlers(
  actions: Pick<AuthActions, "signIn" | "signInLight">,
  {
    readerId,
    hasProgress,
    returnTo,
  }: { readerId: string | null; hasProgress: boolean; returnTo: string },
): SignInHandlers {
  const offered = hasProgress ? (readerId ?? undefined) : undefined;
  return {
    continueWithGoogle: () =>
      actions.signInLight({ returnTo, readerId: offered }),
    switchReader: () =>
      actions.signInLight({ returnTo, readerId: offered, select: true }),
    fullAccount: () =>
      actions.signIn(returnTo, {
        readerId: readerId ?? undefined,
        includeReaders: true,
      }),
  };
}

export function SignInPage({
  store = sessionStore,
  actions = browserAuthActions,
  localProgress = checkLocalProgress,
  progressWaitMs = PROGRESS_WAIT_MS,
  googlePreference = deviceGooglePreference,
  autoGoogleDelayMs = AUTO_GOOGLE_DELAY_MS,
}: {
  googlePreference?: GooglePreference;
  autoGoogleDelayMs?: number;
  store?: SessionStore;
  actions?: Pick<AuthActions, "signIn" | "signInLight">;
  localProgress?: (readerId: string) => Promise<boolean>;
  progressWaitMs?: number;
}) {
  const [searchParams] = useSearchParams();
  const returnTo = safeReturnPath(searchParams.get("returnTo"));
  const errorMessage = signInErrorMessage(
    searchParams.get("error") ?? searchParams.get("auth_error"),
  );
  const session = useSession(store);
  const readerId = readerIdOf(session);
  const serverProgress = useQuery(
    api.fluency.hasProgress,
    readerId === null ? "skip" : { expectedReaderId: readerId },
  );
  const local = useLocalProgress(readerId, localProgress);
  const answer =
    readerId === null ? false : progressAnswer(local, serverProgress);
  const waitedOut = useWaitedOut(answer === undefined, progressWaitMs);
  const hasProgress = progressAfterWait(answer, waitedOut);
  const deciding = session.status === "loading" || hasProgress === undefined;

  const readerMismatch =
    typeof serverProgress === "object" && serverProgress !== null;
  useEffect(() => {
    if (readerMismatch) void store.refresh();
  }, [readerMismatch, store]);

  const { continueWithGoogle, switchReader, fullAccount } = signInHandlers(
    actions,
    { readerId, hasProgress: hasProgress === true, returnTo },
  );

  const [alwaysGoogle, setAlwaysGoogle] = useState(false);
  // After a failed sign-in the person sees what went wrong and chooses.
  const [autoGoogle, setAutoGoogle] = useState(
    () =>
      errorMessage === null &&
      !googlePreference.recentAttempt() &&
      googlePreference.get(),
  );
  const leaveForGoogle = () => {
    googlePreference.markAttempt();
    continueWithGoogle();
  };
  useAutoGoogle(autoGoogle && !deciding, leaveForGoogle, autoGoogleDelayMs);
  useChooserOnRestore(useCallback(() => setAutoGoogle(false), []));
  const chooseGoogle = () => {
    if (alwaysGoogle) googlePreference.remember();
    leaveForGoogle();
  };
  const stopAlwaysGoogle = () => {
    googlePreference.forget();
    setAutoGoogle(false);
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(250,204,21,0.18),_transparent_28%),linear-gradient(180deg,_#fffdf7_0%,_#f5efe2_52%,_#efe6d6_100%)] px-4 py-10 dark:bg-[radial-gradient(circle_at_top,_rgba(251,191,36,0.14),_transparent_30%),linear-gradient(180deg,_#111827_0%,_#0f172a_48%,_#111827_100%)]">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-5xl items-center justify-center">
        <section className="w-full max-w-lg overflow-hidden rounded-[2rem] border border-black/8 bg-white/88 shadow-[0_24px_80px_rgba(15,23,42,0.12)] backdrop-blur dark:border-white/10 dark:bg-slate-950/70">
          <div className="space-y-3 border-b border-black/5 px-6 py-8 sm:px-8 dark:border-white/10">
            <p className="text-xs font-semibold tracking-[0.3em] text-amber-700 uppercase dark:text-amber-300">
              Reader Sign-In
            </p>
            <h1 className="text-3xl leading-tight text-slate-900 sm:text-4xl dark:text-white">
              Keep reading where you left off
            </h1>
          </div>

          {errorMessage !== null && (
            <p
              role="alert"
              className="mx-6 mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 sm:mx-8 dark:border-red-400/30 dark:bg-red-950/40 dark:text-red-200"
            >
              {errorMessage}
            </p>
          )}
          {autoGoogle ? (
            <div className="space-y-4 px-6 py-6 text-center sm:px-8 sm:py-8">
              <output className="block text-sm text-slate-700 dark:text-slate-200">
                Continuing with Google, as this device always does…
              </output>
              <Button
                onClick={leaveForGoogle}
                disabled={deciding}
                data-testid="continue-google-now-cta"
                className="w-full rounded-xl bg-amber-500 text-slate-950 hover:bg-amber-400"
              >
                Continue now
              </Button>
              <Button
                variant="outline"
                onClick={stopAlwaysGoogle}
                data-testid="stop-always-google-cta"
                className="w-full rounded-xl"
              >
                Stop always using Google
              </Button>
            </div>
          ) : (
            <div className="space-y-4 px-6 py-6 sm:px-8 sm:py-8">
              <Button
                onClick={chooseGoogle}
                disabled={deciding}
                data-testid="light-sign-in-cta"
                className="h-auto w-full justify-between gap-3 rounded-2xl bg-amber-500 px-5 py-4 text-left text-slate-950 shadow-sm transition hover:bg-amber-400"
              >
                <span className="space-y-1">
                  <span className="block text-sm font-semibold">
                    Continue with Google
                  </span>
                  <span className="block text-xs opacity-80">
                    Quick sign-in that saves this reader&apos;s progress
                  </span>
                </span>
                <ArrowRightEndOnRectangleIcon className="h-5 w-5 shrink-0" />
              </Button>

              <div className="flex items-center justify-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                <Checkbox
                  id="always-google"
                  checked={alwaysGoogle}
                  onCheckedChange={(checked) =>
                    setAlwaysGoogle(checked === true)
                  }
                  data-testid="always-google"
                />
                <label htmlFor="always-google">
                  Always use Google on this device
                </label>
              </div>

              <p className="text-center text-xs text-slate-500 dark:text-slate-400">
                More than one reader on this Google account?{" "}
                <button
                  type="button"
                  onClick={switchReader}
                  disabled={deciding}
                  data-testid="switch-reader-cta"
                  className="bg-transparent p-0 font-semibold text-amber-700 underline underline-offset-2 hover:text-amber-600 dark:text-amber-300 dark:hover:text-amber-200"
                >
                  Switch or add a reader
                </button>
              </p>

              <div className="rounded-2xl border border-black/10 bg-slate-50/80 p-4 dark:border-white/10 dark:bg-white/5">
                <div className="flex items-start gap-3">
                  <div className="rounded-full bg-amber-100 p-2 text-amber-700 dark:bg-amber-400/15 dark:text-amber-200">
                    <SparklesIcon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 space-y-3">
                    <div className="space-y-1.5">
                      <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
                        Need a family or teacher account?
                      </h2>
                      <ul className="list-disc space-y-1 pl-4 text-xs leading-5 text-slate-600 dark:text-slate-300">
                        <li>Joining the EAR study — orientation needs one</li>
                        <li>
                          Share a reader&apos;s progress with another parent or
                          teacher
                        </li>
                        <li>Add and manage readers and their names</li>
                      </ul>
                    </div>
                    <Button
                      onClick={fullAccount}
                      data-testid="full-sign-in-cta"
                      variant="outline"
                      className="w-full rounded-xl"
                    >
                      Sign in another way
                    </Button>
                  </div>
                </div>
              </div>

              {returnTo !== "/" && (
                <p className="text-center text-xs text-slate-500 dark:text-slate-400">
                  After sign-in you&apos;ll return to{" "}
                  <span className="font-mono">{returnTo}</span>.
                </p>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
